import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Star, CheckSquare, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", category: "", contact_name: "", email: "", phone: "", address: "", rating: 0, notes: "" };

export default function Vendors() {
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/vendors").then((r) => setVendors(r.data));
  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = vendors.length > 0 && selected.length === vendors.length;
  const toggleAll = () => setSelected(allSelected ? [] : vendors.map((v) => v.id));
  const exitSelect = () => { setSelectMode(false); setSelected([]); };
  const askDeleteSingle = (v) => setPendingDelete([v]);
  const askDeleteSelected = () => { const list = vendors.filter((v) => selected.includes(v.id)); if (list.length) setPendingDelete(list); };
  const confirmDelete = async () => {
    if (!pendingDelete?.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/vendors/bulk-delete", { ids: pendingDelete.map((v) => v.id) });
      toast.success(`${data.deleted} vendor${data.deleted === 1 ? "" : "s"} moved to trash`);
      setPendingDelete(null); exitSelect(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({ name: v.name, category: v.category || "", contact_name: v.contact_name || "", email: v.email || "", phone: v.phone || "", address: v.address || "", rating: v.rating || 0, notes: v.notes || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) await api.put(`/vendors/${editing}`, payload);
      else await api.post("/vendors", payload);
      toast.success(editing ? "Vendor updated" : "Vendor added");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader label="Directory" title="Vendors">
        <Button onClick={openNew} className="rounded-none" data-testid="new-vendor-button"><Plus size={16} className="mr-1" /> New Vendor</Button>
      </PageHeader>
      <div className="p-4 sm:p-8">
        {vendors.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="font-heading text-xl font-bold mb-2">No vendors yet</p>
            <p className="text-muted-foreground text-sm">Add vendors to track costs and reuse them in quotations.</p>
          </div>
        ) : (
          <>
          {isManager && (
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="vendors-toolbar">
              {selectMode ? (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-black" data-testid="select-all-vendors" />
                    Select all ({selected.length}/{vendors.length})
                  </label>
                  <div className="flex items-center gap-2">
                    <Button variant="destructive" disabled={!selected.length} onClick={askDeleteSelected} className="rounded-none" data-testid="delete-selected-vendors-button">
                      <Trash size={16} className="mr-1" /> Delete ({selected.length})
                    </Button>
                    <Button variant="outline" onClick={exitSelect} className="rounded-none" data-testid="cancel-select-vendors-button">Cancel</Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectMode(true)} data-testid="select-vendors-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <CheckSquare size={16} /> Select to delete
                  </button>
                  <button onClick={() => navigate("/trash?tab=vendors")} data-testid="view-trash-vendors-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <Trash size={16} /> Trash
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Mobile cards */}
          <div className="md:hidden space-y-3 stagger">
            {vendors.map((v) => (
              <div key={v.id} data-testid={`vendor-card-${v.id}`} onClick={() => selectMode && toggleSelect(v.id)}
                className={`border p-4 ${selectMode ? "cursor-pointer" : ""} ${selectMode && selected.includes(v.id) ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    {selectMode && (
                      <input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggleSelect(v.id)} onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 accent-black shrink-0 mt-1" data-testid={`select-vendor-mobile-${v.id}`} />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate">{v.name}</div>
                      <div className="text-xs text-muted-foreground">{v.category || "—"} · {v.email || v.phone || "—"}</div>
                    </div>
                  </div>
                  {!selectMode && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEdit(v)} className="text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                      {isManager && <button onClick={() => askDeleteSingle(v)} data-testid={`delete-vendor-mobile-${v.id}`} className="text-muted-foreground hover:text-destructive"><Trash size={18} /></button>}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} weight={n <= v.rating ? "fill" : "regular"} className={n <= v.rating ? "text-amber-500" : "text-muted-foreground/40"} />
                    ))}
                  </div>
                  <div className="text-xs font-mono-fig text-muted-foreground">{v.job_count} jobs · {money(v.total_spent)}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="border border-border hidden md:block reveal">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {selectMode && <TableHead className="w-10"></TableHead>}
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                  <TableHead className="text-right">Total Spent</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((v) => (
                  <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}
                    onClick={() => selectMode && toggleSelect(v.id)}
                    className={`hover:bg-secondary transition-colors duration-200 ${selectMode ? "cursor-pointer" : ""} ${selectMode && selected.includes(v.id) ? "bg-secondary" : ""}`}>
                    {selectMode && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggleSelect(v.id)} className="h-4 w-4 accent-black" data-testid={`select-vendor-${v.id}`} />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="font-medium">{v.name}</div>
                      <div className="text-xs text-muted-foreground">{v.email || v.phone || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{v.category || "—"}</TableCell>
                    <TableCell className="text-sm">{v.contact_name || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} size={14} weight={n <= v.rating ? "fill" : "regular"} className={n <= v.rating ? "text-amber-500" : "text-muted-foreground/40"} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{v.job_count}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(v.total_spent)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!selectMode && (
                        <>
                          <button onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`} className="text-muted-foreground hover:text-primary mr-3"><PencilSimple size={16} /></button>
                          {isManager && (
                            <button onClick={() => askDeleteSingle(v)} data-testid={`delete-vendor-${v.id}`} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Vendor" : "New Vendor"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="vendor-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" className="mt-2" />
              </div>
              <div>
                <Label className="label-caps">Category / Trade</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="vendor-category-input" className="mt-2" placeholder="Concrete, Electrical…" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Contact Name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label className="label-caps">Rating (0-5)</Label>
                <Input type="number" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="vendor-rating-input" className="mt-2 font-mono-fig" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label className="label-caps">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
              </div>
            </div>
            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="vendor-submit-button" className="rounded-none">{saving ? "Saving…" : editing ? "Update" : "Add Vendor"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent className="rounded-none" data-testid="delete-vendor-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} weight="fill" className="text-destructive" /> Delete {pendingDelete?.length === 1 ? "vendor" : `${pendingDelete?.length} vendors`}?</DialogTitle></DialogHeader>
          {pendingDelete && (
            <div className="space-y-4">
              <DialogDescription className="text-sm text-muted-foreground">{pendingDelete.length === 1 ? "This vendor" : "These vendors"} will be moved to <strong>Trash</strong>. Restore within 30 days before {pendingDelete.length === 1 ? "it's" : "they're"} permanently removed.</DialogDescription>
              <div className="border border-border max-h-48 overflow-y-auto">
                {pendingDelete.map((v) => (
                  <div key={v.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{v.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{v.job_count} jobs</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPendingDelete(null)} className="rounded-none">Cancel</Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={deleting} className="rounded-none" data-testid="delete-vendor-confirm-button">
                  <Trash size={16} className="mr-1" />{deleting ? "Moving…" : "Move to Trash"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
